import type { HTMLAttributes, ReactNode } from "react";
import styles from "./Input.module.scss";
import { Controller } from "react-hook-form";
import type { Control, RegisterOptions } from "react-hook-form";
import withLabelAndError from "../withLabelAndError";
import { InputAdornment, TextField } from "@mui/material";
import { combineClasses } from "@/lib/utils";

export interface InputProps {
  control?: Control<any>;
  register?: (name: string, options?: RegisterOptions) => Record<string, unknown>;
  validations?: RegisterOptions;
  name: string;
  placeholder?: string | undefined;
  type?: string | undefined;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  className?: string | undefined;
  error?: boolean;
  labelVariant?: "default" | "caption";
  [key: string]: unknown;
}

const Input = ({
  control,
  register,
  validations,
  name,
  placeholder,
  type,
  startIcon,
  endIcon,
  className,
  error,
  ...rest
}: InputProps) => {
  // Native input attributes — TextField would put them on its root div,
  // so they're forwarded to the real <input> via `htmlInput` below.
  const { min, max, maxLength, inputMode, ...textFieldProps } = rest;

  const renderInput = (field: Record<string, unknown> = {}) => (
    <TextField
      {...field}
      {...textFieldProps}
      id={name}
      label=""
      variant="outlined"
      type={type || "text"}
      className={combineClasses(styles.inputRoot, error && styles.error, className)}
      placeholder={placeholder}
      slotProps={{
        input: {
          sx: {
            "&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
              borderColor: "#a0aec0 !important",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px !important",
            },
          },
          startAdornment: startIcon ? (
            <InputAdornment position="start">{startIcon}</InputAdornment>
          ) : null,
          endAdornment: endIcon ? (
            <InputAdornment position="end">{endIcon}</InputAdornment>
          ) : null,
        },
        htmlInput: {
          min: min as number | string | undefined,
          max: max as number | string | undefined,
          maxLength: maxLength as number | undefined,
          // A numeric keyboard on mobile for `type: "text"` number fields —
          // used instead of `type: "number"` where a real `maxLength` is
          // needed (browsers ignore `maxLength` on number inputs).
          inputMode: inputMode as HTMLAttributes<HTMLInputElement>["inputMode"],
          step: "any",
          className: combineClasses(styles.inputField, Boolean(startIcon) && styles.startIcon),
        },
      }}
    />
  );

  if (control) {
    return (
      <Controller
        control={control}
        {...(validations ? { rules: validations } : {})}
        name={name}
        render={({ field }) => renderInput(field)}
      />
    );
  } else if (register) {
    const field = register(name, validations);
    return renderInput(field);
  } else {
    return renderInput();
  }
};

const InputWithLabelAndError = withLabelAndError(Input);

export default InputWithLabelAndError;
export { Input };
